import { useState, useEffect } from "react";
import { Save, Loader2, Plus, Trash2, Sparkles, MessageSquare, FileText, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { getInstructions, updateInstructions } from "@/lib/api";

const TONE_OPTIONS = [
  { value: "warm_professional", label: "Sıcak Profesyonel", desc: "Lüks klinik konsiyerji gibi — güvenilir ve sıcak" },
  { value: "formal", label: "Resmî", desc: "Mesafeli, profesyonel, kısa cümleler" },
  { value: "friendly", label: "Arkadaşça", desc: "Doğal sohbet havası, samimi" },
  { value: "concise", label: "Kısa & Net", desc: "Asla 2 cümleyi geçmez" },
  { value: "luxury", label: "Premium", desc: "Estetik, zarif, seçkin sözcükler" },
];

const LENGTH_OPTIONS = [
  { value: "short", label: "Kısa", desc: "1-2 cümle" },
  { value: "balanced", label: "Dengeli", desc: "2-4 cümle (varsayılan)" },
  { value: "detailed", label: "Detaylı", desc: "4-8 cümle, madde işaretli" },
];

export default function InstructionsTab() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getInstructions().then((d) => setData({
      ...d,
      tone: d.tone || "warm_professional",
      style_notes: d.style_notes || "",
      response_length: d.response_length || "balanced",
      address_form: d.address_form || "siz",
      templates: d.templates || [],
    }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await updateInstructions(data);
      toast.success("Kaydedildi");
    } catch { toast.error("Hata"); } finally { setSaving(false); }
  };

  const addTemplate = () => {
    setData({ ...data, templates: [...data.templates, { title: "", trigger: "", response: "" }] });
  };
  const updateTemplate = (i, field, val) => {
    const next = [...data.templates];
    next[i] = { ...next[i], [field]: val };
    setData({ ...data, templates: next });
  };
  const removeTemplate = (i) => {
    setData({ ...data, templates: data.templates.filter((_, x) => x !== i) });
  };

  if (!data) return <Loader2 className="w-5 h-5 animate-spin mx-auto" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg">AI Komutları</h2>
          <p className="text-xs text-muted-foreground">Yapay zekanın tarzı, yaklaşımı ve sabit yanıtlarını burada yönetin.</p>
        </div>
        <Button data-testid="instr-save" onClick={save} disabled={saving} style={{ background: "#B39B7A" }}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Tüm Değişiklikleri Kaydet
        </Button>
      </div>

      <Tabs defaultValue="style" className="w-full">
        <TabsList className="bg-secondary/60 p-1 rounded-2xl flex flex-wrap h-auto gap-1">
          <TabsTrigger value="style" data-testid="instr-tab-style" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm font-display text-sm h-9 px-4">
            <Wand2 className="w-4 h-4 mr-2" /> Tarz & Yaklaşım
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="instr-tab-templates" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm font-display text-sm h-9 px-4">
            <MessageSquare className="w-4 h-4 mr-2" /> Sabit Yanıt Şablonları
          </TabsTrigger>
          <TabsTrigger value="advanced" data-testid="instr-tab-advanced" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm font-display text-sm h-9 px-4">
            <FileText className="w-4 h-4 mr-2" /> Gelişmiş (Sistem Talimatı)
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: Style & Tone */}
        <TabsContent value="style" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-display flex items-center gap-2"><Sparkles className="w-4 h-4" /> Konuşma Tarzı</CardTitle>
              <p className="text-xs text-muted-foreground">AI'nın genel ses tonu — her yanıtına yansır.</p>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {TONE_OPTIONS.map((o) => {
                  const active = data.tone === o.value;
                  return (
                    <button
                      key={o.value}
                      data-testid={`tone-${o.value}`}
                      onClick={() => setData({ ...data, tone: o.value })}
                      className={`text-left p-4 rounded-2xl border transition-all ${
                        active ? "border-primary bg-primary/5 shadow-sm" : "border-border/40 hover:border-primary/40"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-display font-medium text-sm">{o.label}</div>
                        {active && <Badge style={{ background: "#B39B7A" }} className="text-[10px]">seçili</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">{o.desc}</div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-display">Yanıt Uzunluğu</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup value={data.response_length} onValueChange={(v) => setData({ ...data, response_length: v })}>
                  {LENGTH_OPTIONS.map((o) => (
                    <div key={o.value} className="flex items-start space-x-3 p-2 rounded-lg hover:bg-secondary/50">
                      <RadioGroupItem value={o.value} id={`len-${o.value}`} data-testid={`length-${o.value}`} className="mt-1" />
                      <Label htmlFor={`len-${o.value}`} className="cursor-pointer flex-1">
                        <div className="font-medium text-sm">{o.label}</div>
                        <div className="text-xs text-muted-foreground">{o.desc}</div>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-display">Hitap Şekli (Türkçe)</CardTitle>
                <p className="text-xs text-muted-foreground">Türkçe konuşmalarda ziyaretçiye nasıl hitap edilsin?</p>
              </CardHeader>
              <CardContent>
                <RadioGroup value={data.address_form} onValueChange={(v) => setData({ ...data, address_form: v })}>
                  <div className="flex items-start space-x-3 p-2 rounded-lg hover:bg-secondary/50">
                    <RadioGroupItem value="siz" id="addr-siz" data-testid="addr-siz" className="mt-1" />
                    <Label htmlFor="addr-siz" className="cursor-pointer flex-1">
                      <div className="font-medium text-sm">"Siz" (saygılı)</div>
                      <div className="text-xs text-muted-foreground">Resmî ve klinik için uygun — varsayılan</div>
                    </Label>
                  </div>
                  <div className="flex items-start space-x-3 p-2 rounded-lg hover:bg-secondary/50">
                    <RadioGroupItem value="sen" id="addr-sen" data-testid="addr-sen" className="mt-1" />
                    <Label htmlFor="addr-sen" className="cursor-pointer flex-1">
                      <div className="font-medium text-sm">"Sen" (samimi)</div>
                      <div className="text-xs text-muted-foreground">Daha gündelik, genç hedef kitle için</div>
                    </Label>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="font-display">AI'nın Yaklaşımı (Serbest Notlar)</CardTitle>
              <p className="text-xs text-muted-foreground">
                AI'ya nasıl davranmasını istediğinizi anlatın. Örn: "Önce empati kur, sonra çözüm öner. Asla agresif satış yapma. Soru soranı yargılama."
              </p>
            </CardHeader>
            <CardContent>
              <Textarea
                data-testid="style-notes"
                rows={6}
                value={data.style_notes}
                onChange={(e) => setData({ ...data, style_notes: e.target.value })}
                placeholder="Ör: Cevap vermeden önce ziyaretçinin endişesini özetle anla. Asla 'kesinlikle' / 'mucize' gibi tıbbi vaatler verme. Her zaman alternatif sun..."
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 2: Templates */}
        <TabsContent value="templates" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-display flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Sabit Yanıt Şablonları</CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    Sık sorulan sorular için sabit yanıtlar. AI bunları temel alıp kullanıcının diline çevirerek ve seçtiğiniz tarza uyarlayarak söyler.
                  </p>
                </div>
                <Button onClick={addTemplate} data-testid="tpl-add" style={{ background: "#8B9B88" }}>
                  <Plus className="w-4 h-4 mr-2" /> Şablon Ekle
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {data.templates.length === 0 && (
                <div className="text-center text-muted-foreground text-sm py-8 border border-dashed border-border/40 rounded-xl">
                  Henüz şablon yok. Sık sorulan sorular için sabit yanıtlar ekleyin.
                </div>
              )}
              {data.templates.map((tpl, i) => (
                <div key={i} data-testid={`tpl-${i}`} className="p-4 border border-border/40 rounded-2xl space-y-3 bg-secondary/30">
                  <div className="flex items-center justify-between">
                    <div className="font-display text-sm text-muted-foreground">Şablon #{i + 1}</div>
                    <Button variant="ghost" size="icon" onClick={() => removeTemplate(i)} data-testid={`tpl-del-${i}`} className="text-destructive">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div>
                    <Label className="text-xs">Başlık (sadece yönetici için)</Label>
                    <Input
                      data-testid={`tpl-title-${i}`}
                      placeholder="Ör: Randevu nasıl alınır?"
                      value={tpl.title}
                      onChange={(e) => updateTemplate(i, "title", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Tetikleyici Konu (kullanıcı bu konuyu sorduğunda)</Label>
                    <Input
                      data-testid={`tpl-trigger-${i}`}
                      placeholder="Ör: randevu alma, randevu süreci, nasıl gelirim"
                      value={tpl.trigger}
                      onChange={(e) => updateTemplate(i, "trigger", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Yanıt (AI bunu temel alır, kullanıcının diline çevirir)</Label>
                    <Textarea
                      data-testid={`tpl-response-${i}`}
                      rows={4}
                      placeholder="Ör: Randevular Pazartesi-Cuma 09:00-18:00 arası 0212 232 37 48'den alınır. Ön görüşme ücretsizdir."
                      value={tpl.response}
                      onChange={(e) => updateTemplate(i, "response", e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="bg-secondary/40 border-border/30">
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <Sparkles className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div className="text-xs text-muted-foreground space-y-1">
                  <p><strong>Şablon ipucu:</strong> AI, kullanıcının sorduğu konuyla şablonun "tetikleyici konu" alanını eşleştirir.</p>
                  <p>Türkçe yazsanız bile, AI başka dildeki kullanıcılara o dilde — ve seçtiğiniz tarzda — bu yanıtı söyler.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 3: Advanced */}
        <TabsContent value="advanced" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-display">Ek Komutlar / Kurallar</CardTitle>
              <p className="text-xs text-muted-foreground">
                Özel kurallar. Örn: "Operasyon detaylarını verme", "Yan etkilerden bahsetme, klinikten yönlendir", "Acil durum sorularında 112'ye yönlendir".
              </p>
            </CardHeader>
            <CardContent>
              <Textarea
                data-testid="instr-extra"
                rows={8}
                value={data.extra_rules}
                onChange={(e) => setData({ ...data, extra_rules: e.target.value })}
                placeholder="- Asla operasyon (cerrahi) detayı verme&#10;- Yan etki sorularında klinikle görüşmeye yönlendir&#10;- Hamilelik sorularında 'klinikle görüşmenizi öneririm' de"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-display">Sistem Prompt'u (uzman ayarı)</CardTitle>
              <p className="text-xs text-muted-foreground">AI'nın temel kişiliği. Bozulduğunda sıfırlamak için boş bırakıp kaydedebilirsiniz.</p>
            </CardHeader>
            <CardContent>
              <Textarea
                data-testid="instr-system"
                rows={14}
                value={data.system_prompt}
                onChange={(e) => setData({ ...data, system_prompt: e.target.value })}
                className="font-mono text-xs"
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
